import type {
  EffectiveProductCapabilityState,
  ProductCapabilityKey,
} from './capabilities';

export type ExperienceAudience = 'planner' | 'guest' | 'vendor';

export type ExperienceLevel = 'simple' | 'advanced';

export type VisibilityMode = 'guided' | 'full';

export type ConfigurationDepth = 'streamlined' | 'full';

export type ProgressiveDisclosureState =
  'collapsed' | 'contextual' | 'expanded';

export type SimplifiedSurfaceTreatment =
  'visible' | 'simplified' | 'hidden' | 'disabled' | 'auto_defaulted';

export type ExperienceFeatureKey =
  | 'planner.workflows'
  | 'planner.localization'
  | 'planner.publishing'
  | 'planner.integrations'
  | 'planner.vendor_access'
  | 'planner.advanced_settings'
  | 'guest.shared_planning'
  | 'guest.gallery'
  | 'guest.schedule'
  | 'guest.seating'
  | 'vendor.deliverables'
  | 'vendor.updates'
  | 'vendor.settings';

export interface ExperienceFeatureTreatment {
  featureKey: ExperienceFeatureKey;
  treatment: SimplifiedSurfaceTreatment;
  reason: string;
  capabilityKeys: ProductCapabilityKey[];
}

export interface ExperienceProfileDebug {
  recommendedExperienceLevel: ExperienceLevel;
  reasons: string[];
}

export interface ExperienceProfileAnalytics {
  profileKey: `${ExperienceAudience}.${ExperienceLevel}`;
  surfaceCount: number;
}

export interface ExperienceProfile {
  audience: ExperienceAudience;
  experienceLevel: ExperienceLevel;
  visibilityMode: VisibilityMode;
  configurationDepth: ConfigurationDepth;
  progressiveDisclosureState: ProgressiveDisclosureState;
  featureTreatments: Record<ExperienceFeatureKey, ExperienceFeatureTreatment>;
  debug: ExperienceProfileDebug;
  analytics: ExperienceProfileAnalytics;
}

export interface ExperienceProfileInput {
  audience: ExperienceAudience;
  capabilityStates: Record<
    ProductCapabilityKey,
    EffectiveProductCapabilityState
  >;
}

const advancedPlannerCapabilityKeys: ProductCapabilityKey[] = [
  'multi_language',
  'publishing',
  'api_access',
  'webhooks',
  'vendor_access',
];

function createTreatment(
  featureKey: ExperienceFeatureKey,
  treatment: SimplifiedSurfaceTreatment,
  reason: string,
  capabilityKeys: ProductCapabilityKey[] = [],
): ExperienceFeatureTreatment {
  return {
    featureKey,
    treatment,
    reason,
    capabilityKeys,
  };
}

export function resolveExperienceLevel(
  audience: ExperienceAudience,
  capabilityStates: Record<
    ProductCapabilityKey,
    EffectiveProductCapabilityState
  >,
): ExperienceLevel {
  if (audience !== 'planner') {
    return 'simple';
  }

  return advancedPlannerCapabilityKeys.some(
    (capabilityKey) => capabilityStates[capabilityKey]?.enabled,
  )
    ? 'advanced'
    : 'simple';
}

export function resolveExperienceProfile(
  input: ExperienceProfileInput,
): ExperienceProfile {
  const { audience, capabilityStates } = input;
  const experienceLevel = resolveExperienceLevel(audience, capabilityStates);
  const reasons: string[] = [];

  if (experienceLevel === 'advanced') {
    reasons.push(
      'Advanced planner mode is enabled because one or more premium capabilities are active on the occasion.',
    );
  } else {
    reasons.push(
      audience === 'planner'
        ? 'Simple planner mode keeps advanced capability surfaces collapsed until the occasion enables them.'
        : 'This audience defaults to a simplified, guided experience.',
    );
  }

  const featureTreatments: Record<
    ExperienceFeatureKey,
    ExperienceFeatureTreatment
  > = {
    'planner.workflows': createTreatment(
      'planner.workflows',
      'visible',
      'Guided workflows remain the primary control center in both simple and advanced planner modes.',
    ),
    'planner.localization': capabilityStates.multi_language.enabled
      ? createTreatment(
          'planner.localization',
          experienceLevel === 'advanced' ? 'visible' : 'simplified',
          experienceLevel === 'advanced'
            ? 'Multi-language authoring is enabled and exposed in full.'
            : 'Multi-language is enabled, but the UI should keep authoring entry points streamlined.',
          ['multi_language'],
        )
      : createTreatment(
          'planner.localization',
          'auto_defaulted',
          'Single-locale operation is auto-defaulted until the multi_language capability is enabled.',
          ['multi_language'],
        ),
    'planner.publishing': capabilityStates.publishing.enabled
      ? createTreatment(
          'planner.publishing',
          experienceLevel === 'advanced' ? 'visible' : 'simplified',
          'Publishing remains available, with publishing-specific controls simplified unless the planner is in advanced mode.',
          ['publishing'],
        )
      : createTreatment(
          'planner.publishing',
          'simplified',
          'Publishing remains available, but publishing generation stays hidden when the publishing capability is disabled.',
          ['publishing'],
        ),
    'planner.integrations': capabilityStates.api_access.enabled
      ? createTreatment(
          'planner.integrations',
          experienceLevel === 'advanced' ? 'visible' : 'simplified',
          'Integration configuration is enabled, but should collapse advanced connection details in simple mode.',
          ['api_access'],
        )
      : createTreatment(
          'planner.integrations',
          'hidden',
          'Integrations should be removed from primary planner navigation until API access is enabled.',
          ['api_access'],
        ),
    'planner.vendor_access': capabilityStates.vendor_access.enabled
      ? createTreatment(
          'planner.vendor_access',
          experienceLevel === 'advanced' ? 'visible' : 'simplified',
          'Vendor coordination is enabled, with simplified entry points in simple mode.',
          ['vendor_access'],
        )
      : createTreatment(
          'planner.vendor_access',
          'hidden',
          'Vendor coordination should stay out of the primary planner path until vendor access is enabled.',
          ['vendor_access'],
        ),
    'planner.advanced_settings':
      experienceLevel === 'advanced'
        ? createTreatment(
            'planner.advanced_settings',
            'visible',
            'Advanced planner mode keeps deeper settings exposed.',
          )
        : createTreatment(
            'planner.advanced_settings',
            'simplified',
            'Simple mode should collapse expert-level settings behind guided defaults and focused next steps.',
          ),
    'guest.shared_planning': createTreatment(
      'guest.shared_planning',
      'simplified',
      'Guest collaboration should remain available only as a lightweight guided task surface.',
    ),
    'guest.gallery': createTreatment(
      'guest.gallery',
      'visible',
      'Gallery remains a first-class guest surface when the module is present.',
    ),
    'guest.schedule': createTreatment(
      'guest.schedule',
      'visible',
      'Schedule remains visible, but should stay guest-safe and summary-first.',
    ),
    'guest.seating': createTreatment(
      'guest.seating',
      'visible',
      'Seating stays visible only as a published guest summary.',
    ),
    'vendor.deliverables': createTreatment(
      'vendor.deliverables',
      capabilityStates.vendor_access.enabled ? 'visible' : 'disabled',
      capabilityStates.vendor_access.enabled
        ? 'Vendor deliverables remain available in the vendor workspace.'
        : 'Vendor deliverables should stay disabled until vendor access is enabled.',
      ['vendor_access'],
    ),
    'vendor.updates': createTreatment(
      'vendor.updates',
      capabilityStates.vendor_access.enabled ? 'visible' : 'disabled',
      capabilityStates.vendor_access.enabled
        ? 'Vendor updates remain available in the vendor workspace.'
        : 'Vendor updates should stay disabled until vendor access is enabled.',
      ['vendor_access'],
    ),
    'vendor.settings': createTreatment(
      'vendor.settings',
      'hidden',
      'Vendor surfaces should avoid planner-style settings depth.',
    ),
  };

  return {
    audience,
    experienceLevel,
    visibilityMode:
      audience === 'planner' && experienceLevel === 'advanced'
        ? 'full'
        : 'guided',
    configurationDepth:
      audience === 'planner' && experienceLevel === 'advanced'
        ? 'full'
        : 'streamlined',
    progressiveDisclosureState:
      audience === 'planner' && experienceLevel === 'advanced'
        ? 'expanded'
        : audience === 'planner'
          ? 'contextual'
          : 'collapsed',
    featureTreatments,
    debug: {
      recommendedExperienceLevel: experienceLevel,
      reasons,
    },
    analytics: {
      profileKey: `${audience}.${experienceLevel}`,
      surfaceCount: Object.values(featureTreatments).filter(
        (feature) => feature.treatment !== 'hidden',
      ).length,
    },
  };
}

export function shouldRenderSurface(
  profile: ExperienceProfile,
  featureKey: ExperienceFeatureKey,
) {
  const treatment = profile.featureTreatments[featureKey];

  return treatment.treatment !== 'hidden';
}

export function shouldDisableSurface(
  profile: ExperienceProfile,
  featureKey: ExperienceFeatureKey,
) {
  return profile.featureTreatments[featureKey].treatment === 'disabled';
}

export function isSimplifiedSurface(
  profile: ExperienceProfile,
  featureKey: ExperienceFeatureKey,
) {
  return ['simplified', 'auto_defaulted'].includes(
    profile.featureTreatments[featureKey].treatment,
  );
}
